import { useState, useEffect } from 'react';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import {
  HiOutlineDownload,
  HiOutlineRefresh,
  HiOutlineCalendar,
} from 'react-icons/hi';

const fmtBDT = (n) => `৳${Number(n || 0).toLocaleString('en-IN')}`;

// Current month as YYYY-MM (local time — avoids UTC shift bugs).
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const MonthlyReport = () => {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await API.get('/admin/reports/monthly', {
          params: { month },
        });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || 'রিপোর্ট আনতে ব্যর্থ হয়েছে।');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReport();
    return () => { cancelled = true; };
  }, [month]);

  const downloadExcel = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${window.location.origin}/api/admin/reports/monthly/export?month=${month}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `monthly_report_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error('Excel download error:', e);
      alert('Excel ডাউনলোড ব্যর্থ হয়েছে।');
    } finally {
      setDownloading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const summary = data?.summary || null;
  const workers = data?.per_worker || [];
  const sites = data?.per_site || [];

  // Summary cards config — (label, value, icon, badge colors)
  const cards = summary ? [
    { label: 'মোট ওয়ার্কার', value: Number(summary.workers || 0).toLocaleString('en-IN'), icon: '👷', iconBg: 'bg-brand-100 dark:bg-brand-900/40', textCls: 'text-brand-700 dark:text-brand-300' },
    { label: 'মোট দিন', value: Number(summary.total_days || 0).toLocaleString('en-IN'), icon: '📅', iconBg: 'bg-ink-100 dark:bg-ink-800', textCls: 'text-ink-900 dark:text-white' },
    { label: 'গ্রস বেতন', value: fmtBDT(summary.gross), icon: '💰', iconBg: 'bg-emerald-100 dark:bg-emerald-900/40', textCls: 'text-emerald-700 dark:text-emerald-300' },
    { label: 'অগ্রিম', value: fmtBDT(summary.advance), icon: '💸', iconBg: 'bg-violet-100 dark:bg-violet-900/40', textCls: 'text-violet-700 dark:text-violet-300' },
    { label: 'নিট বেতন', value: fmtBDT(summary.net), icon: '🧾', iconBg: 'bg-brand-100 dark:bg-brand-900/40', textCls: 'text-brand-700 dark:text-brand-300' },
    { label: 'পরিশোধিত', value: fmtBDT(summary.paid), icon: '✅', iconBg: 'bg-emerald-100 dark:bg-emerald-900/40', textCls: 'text-emerald-700 dark:text-emerald-300' },
    { label: 'বকেয়া', value: fmtBDT(summary.unpaid), icon: '⏳', iconBg: 'bg-amber-100 dark:bg-amber-900/40', textCls: 'text-amber-700 dark:text-amber-300' },
    { label: 'মোট সাইট', value: Number(summary.site_count || 0).toLocaleString('en-IN'), icon: '🏗️', iconBg: 'bg-sky-100 dark:bg-sky-900/40', textCls: 'text-sky-700 dark:text-sky-300' },
  ] : [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="মাসিক রিপোর্ট"
        subtitle={data?.month?.label || month}
        action={
          <div className="flex items-center gap-2">
            <div className="relative">
              <HiOutlineCalendar className="w-4 h-4 text-ink-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="month"
                value={month}
                onChange={(e) => e.target.value && setMonth(e.target.value)}
                className="input-field text-sm pl-9"
                aria-label="মাস নির্বাচন"
              />
            </div>
            <button
              type="button"
              onClick={downloadExcel}
              disabled={downloading || !summary}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              title="এই মাসের Excel রিপোর্ট ডাউনলোড করুন"
            >
              {downloading ? <HiOutlineRefresh className="w-4 h-4 animate-spin" /> : <HiOutlineDownload className="w-4 h-4" />}
              📥 Excel ডাউনলোড
            </button>
          </div>
        }
      />

      {error ? (
        <div className="card">
          <EmptyState
            icon="⚠️"
            title="রিপোর্ট লোড করতে সমস্যা হয়েছে"
            description={error}
            action={
              <button
                type="button"
                onClick={() => { setMonth(currentMonth()); }}
                className="btn-primary"
              >
                <HiOutlineRefresh className="w-5 h-5" />
                পুনরায় চেষ্টা করুন
              </button>
            }
          />
        </div>
      ) : !summary ? (
        <div className="card">
          <EmptyState
            icon="📊"
            title={`${month} মাসে এখনো কোনো তথ্য নেই`}
            description={`${month} মাসের জন্য কোনো ওয়ার্কার বা সাইটের রেকর্ড পাওয়া যায়নি।`}
          />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {cards.map((c) => (
              <div className="card" key={c.label}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider font-medium truncate">{c.label}</p>
                    <p className={`text-xl sm:text-2xl font-bold mt-1 truncate ${c.textCls}`}>{c.value}</p>
                  </div>
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 ${c.iconBg}`}>
                    {c.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Worker breakdown */}
          <div className="card p-0 overflow-hidden">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-brand-500" />
                ওয়ার্কার ভাঙচ
              </h2>
              <span className="text-xs text-ink-500 dark:text-ink-400">{workers.length} জন</span>
            </div>
            {workers.length === 0 ? (
              <EmptyState icon="👷" title="এই মাসে কোনো ওয়ার্কার নেই" description={`${month} মাসের জন্য কোনো ওয়ার্কার রেকর্ড পাওয়া যায়নি।`} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-ink-50 dark:bg-ink-900/30 border-y border-ink-100 dark:border-ink-700">
                    <tr>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">ওয়ার্কার</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">উপ/আধা/অনু</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">মোট দিন</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">গ্রস</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">অগ্রিম</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">নিট</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">পরিশোধিত</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">বকেয়া</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                    {workers.map((w) => {
                      const hasAdvance = Number(w.advance) > 0;
                      const unpaid = Number(w.unpaid) > 0;
                      return (
                        <tr key={w.id} className="transition-colors hover:bg-ink-50 dark:hover:bg-ink-900/30">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-gradient-brand flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                {(w.name || '?').charAt(0)}
                              </div>
                              <div className="min-w-0">
                                <span className="font-semibold text-ink-900 dark:text-white block truncate">{w.name}</span>
                                {w.phone && (
                                  <span className="text-[11px] text-ink-500 dark:text-ink-400 block truncate">{w.phone}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 text-center whitespace-nowrap">
                            <span className="text-emerald-600 font-semibold">{Number(w.present || 0)}</span>
                            <span className="text-ink-400"> / </span>
                            <span className="text-amber-600 font-semibold">{Number(w.half || 0)}</span>
                            <span className="text-ink-400"> / </span>
                            <span className="text-rose-600 font-semibold">{Number(w.absent || 0)}</span>
                          </td>
                          <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 text-center font-medium">{Number(w.total_days || 0).toFixed(1)}</td>
                          <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 text-right whitespace-nowrap">{fmtBDT(w.gross)}</td>
                          <td className={`px-5 py-3 text-sm font-semibold text-right whitespace-nowrap ${hasAdvance ? 'text-violet-600 dark:text-violet-400' : 'text-ink-400'}`}>
                            {hasAdvance ? `−${fmtBDT(w.advance)}` : '—'}
                          </td>
                          <td className="px-5 py-3 text-sm font-bold text-brand-700 dark:text-brand-300 text-right whitespace-nowrap">{fmtBDT(w.net)}</td>
                          <td className={`px-5 py-3 text-sm font-semibold text-right whitespace-nowrap ${Number(w.amount_paid) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-ink-400'}`}>
                            {Number(w.amount_paid) > 0 ? fmtBDT(w.amount_paid) : '—'}
                          </td>
                          <td className="px-5 py-3 text-sm font-semibold text-right whitespace-nowrap">
                            {unpaid ? (
                              <Badge variant="warning" dot>{fmtBDT(w.unpaid)}</Badge>
                            ) : (
                              <Badge variant="success">পরিশোধিত</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Site breakdown */}
          <div className="card p-0 overflow-hidden">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                সাইট ভাঙচ
              </h2>
              <span className="text-xs text-ink-500 dark:text-ink-400">{sites.length} টি</span>
            </div>
            {sites.length === 0 ? (
              <EmptyState icon="🏗️" title="এই মাসে কোনো সাইট নেই" description={`${month} মাসের জন্য কোনো সাইট রেকর্ড পাওয়া যায়নি।`} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-ink-50 dark:bg-ink-900/30 border-y border-ink-100 dark:border-ink-700">
                    <tr>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">সাইট</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">ওয়ার্কার</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">মোট দিন</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">খরচ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                    {sites.map((s) => (
                      <tr key={s.id} className="transition-colors hover:bg-ink-50 dark:hover:bg-ink-900/30">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-accent-100 dark:bg-accent-900/40 flex items-center justify-center text-base flex-shrink-0">🏗️</div>
                            <span className="font-semibold text-ink-900 dark:text-white block truncate">{s.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 text-center font-medium">{Number(s.worker_count || 0)}</td>
                        <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 text-center font-medium">{Number(s.total_days || 0).toFixed(1)}</td>
                        <td className="px-5 py-3 text-sm font-semibold text-ink-900 dark:text-white text-right whitespace-nowrap">{fmtBDT(s.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MonthlyReport;